import type { PageMetadata } from "@moment/api-collab/api-types";

const SLUG_RE = /[^0-9a-zA-Z]+/g;
const SLUG_CHAR_LIMIT = 255;

export const sluggify = (s: string): string => {
	const slug = s.toLowerCase().replace(SLUG_RE, " ").trim().replace(/\s/g, "-");
	return slug.length > SLUG_CHAR_LIMIT ? slug.substring(0, SLUG_CHAR_LIMIT) : slug;
};

export const generateUniqueSlug = (
	baseSlug: string,
	pageId: string,
	parentPageID: string | null | undefined,
	pages: PageMetadata[]
): string => {
	const siblings = pages.filter((p) => {
		if (p.id === pageId) {
			return false;
		}

		if (parentPageID == null) {
			return p.parentPageID == null;
		}

		return p.parentPageID === parentPageID;
	});
	const siblingSlugSet = new Set(siblings.map((p) => p.slug));

	if (!siblingSlugSet.has(baseSlug)) {
		return baseSlug;
	}

	let suffix = 2;
	while (siblingSlugSet.has(`${baseSlug}-${suffix}`)) {
		suffix++;
	}
	return `${baseSlug}-${suffix}`;
};

/**
 * Build the full slug path for a page by traversing up the parent chain.
 * Returns the complete path like "parent/child/grandchild" for nested pages.
 */
export const buildFullSlugPath = (pages: PageMetadata[], page: PageMetadata): string => {
	const slugParts: string[] = [];
	let currentPage: PageMetadata | undefined = page;
	const visited = new Set<string>();

	while (currentPage) {
		if (visited.has(currentPage.id)) {
			break;
		}
		visited.add(currentPage.id);
		slugParts.unshift(currentPage.slug);
		currentPage = currentPage.parentPageID
			? pages.find((p) => p.id === currentPage!.parentPageID)
			: undefined;
	}

	return slugParts.join("/");
};

/**
 * Build the default on-disk page file path from slug hierarchy when metadata
 * does not include an explicit `path`.
 */
export const buildDefaultPagePath = (
	pages: PageMetadata[],
	page: PageMetadata,
	filename = page.type === "request" ? "index.yml" : "index.md"
): string => {
	const slugPath = buildFullSlugPath(pages, page);
	return `pages/${slugPath}/${filename}`;
};

/**
 * Find a page by traversing the page tree following parent-child relationships.
 * Each slug segment corresponds to a page at that level in the hierarchy.
 *
 * For slug path "parent/child/grandchild":
 * - Find root page with slug "parent"
 * - Find its child with slug "child"
 * - Find that child's child with slug "grandchild"
 */
export const findPageBySlugPath = (
	pages: PageMetadata[],
	pageSlug: string
): PageMetadata | undefined => {
	// Convert to array of segments
	const slugSegments = Array.isArray(pageSlug)
		? pageSlug
		: pageSlug.split("/").filter((s) => s.length > 0);

	if (slugSegments.length === 0) {
		// No slug - return index page or first root page
		const indexPage = pages.find((page) => page.slug === "index");
		if (indexPage) {
			return indexPage;
		}

		const firstRootPage = pages.find((page) => !page.parentPageID);
		return firstRootPage;
	}

	// Priority 1: Try to find by full joined slug (for legacy pages with slashes in slug)
	// Only match root-level pages to avoid accidentally matching child pages
	const pageByJoinedSlug = pages.find((page) => page.slug === pageSlug && !page.parentPageID);
	if (pageByJoinedSlug) {
		return pageByJoinedSlug;
	}

	// Priority 2: Try to traverse the tree by following parent-child relationships
	let currentParentId: string | null = null;
	let currentPage: PageMetadata | undefined;

	for (const slug of slugSegments) {
		currentPage = pages.find((page) => {
			if (page.slug !== slug) {
				return false;
			}

			if (currentParentId === null) {
				return page.parentPageID == null;
			}

			// For child pages, parent IDs should match exactly
			return page.parentPageID === currentParentId;
		});

		if (!currentPage) {
			// Tree traversal failed at this segment
			currentPage = undefined;
			break;
		}

		currentParentId = currentPage.id;
	}

	if (currentPage) {
		return currentPage;
	}

	return pages.find((page) => page.id === pageSlug);
};
